import type { Dispatch, ReactNode } from 'react';
import { createContext, useContext, useMemo, useRef } from 'react';

import type { DraftSteps } from '../utils/types';

type DraftStepSubmitListener = Dispatch<DraftSteps>;

type DraftStepSubmitBusContextProps = {
  onSuccessfulSubmit: (fn: DraftStepSubmitListener) => VoidFunction;
  emitSuccessfulSubmit: (step: DraftSteps) => Promise<void>;
};

const DraftStepSubmitBusContext = createContext(
  {} as DraftStepSubmitBusContextProps
);

type DraftStepSubmitBusProviderProps = {
  children: ReactNode;
};

/**
 * Event bus for listening to form step submits to decouple code
 * This could be extended to also listen to submit failures to handle errors separately
 * such as showing notifications, etc.
 */
export const DraftStepSubmitBusProvider = ({
  children
}: DraftStepSubmitBusProviderProps) => {
  const onSuccessListenersRef = useRef(new Set<DraftStepSubmitListener>());

  const value = useMemo(
    () => ({
      onSuccessfulSubmit: (fn: DraftStepSubmitListener) => {
        onSuccessListenersRef.current.add(fn);
        return () => onSuccessListenersRef.current.delete(fn);
      },
      emitSuccessfulSubmit: async (step: DraftSteps) => {
        onSuccessListenersRef.current.forEach((fn) => fn(step));
      }
    }),
    []
  );

  return (
    <DraftStepSubmitBusContext.Provider value={value}>
      {children}
    </DraftStepSubmitBusContext.Provider>
  );
};

export const useDraftStepSubmitBus = () =>
  useContext(DraftStepSubmitBusContext);
